/**
 * SkillTreeOverlay.js
 *
 * June 2026 — Skill Tree (Neural Console station terminal).
 *
 * Players spend skill points to unlock tiered upgrades across three branches.
 * Points can be bought with credits. Unlocking recomputes derived stats on the
 * server; the parent refreshes player state + progression multipliers.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Brain, Plus, Check, Heart, Shield, Wind, Footprints, Rocket, ArrowUpCircle, Package, Pickaxe, DoorOpen } from 'lucide-react';

const BRANCH_ORDER = ['Survival', 'Mobility', 'Logistics'];
const BRANCH_TONE = {
  Survival: { ring: 'border-rose-400/40', dot: 'bg-rose-400', text: 'text-rose-200' },
  Mobility: { ring: 'border-cyan-400/40', dot: 'bg-cyan-400', text: 'text-cyan-200' },
  Logistics: { ring: 'border-emerald-400/40', dot: 'bg-emerald-400', text: 'text-emerald-200' },
};
const SKILL_ICON = {
  vitality: Heart, shielding: Shield, life_support: Wind,
  fleet_footed: Footprints, ace_pilot: Rocket, low_gravity_legs: ArrowUpCircle,
  cargo_expansion: Package, scavenger: Pickaxe,
};

export default function SkillTreeOverlay({ open, api, credits, onClose, onChanged, updateCredits }) {
  const [tree, setTree] = useState([]);
  const [skills, setSkills] = useState({});
  const [points, setPoints] = useState(0);
  const [price, setPrice] = useState(5000);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/skills/tree');
      setTree(data?.tree || []);
      setSkills(data?.skills || {});
      setPoints(data?.skill_points ?? 0);
      setPrice(data?.point_price ?? 5000);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const handleUnlock = useCallback(async (node) => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/skills/unlock', { skill_id: node.id });
      setSkills(data?.skills || skills);
      setPoints(data?.skill_points ?? points);
      toast.success(`${node.name} → Lv ${data?.skills?.[node.id]}`);
      onChanged?.(data?.bonuses);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unlock failed');
    } finally { setBusy(false); }
  }, [api, skills, points, onChanged]);

  const handleBuyPoints = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/skills/buy-points', { count: 1 });
      if (data?.credits != null) updateCredits?.(data.credits);
      setPoints(data?.skill_points ?? points);
      toast.success(`+1 skill point · −${price.toLocaleString()} cr`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Not enough credits');
    } finally { setBusy(false); }
  }, [api, price, points, updateCredits]);

  const byBranch = useMemo(() => {
    const g = {};
    for (const n of tree) (g[n.branch] = g[n.branch] || []).push(n);
    return g;
  }, [tree]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="skilltree-overlay"
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-teal-400/25 bg-[#070f0e]/95 shadow-[0_0_60px_rgba(45,212,191,0.18)] flex flex-col"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-teal-300" />
            <div>
              <h2 className="text-lg font-semibold text-teal-100 tracking-wide">Neural Console · Skill Tree</h2>
              <p className="text-[11px] text-slate-400">Spend skill points to upgrade your pilot</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 rounded-lg bg-teal-400/10 border border-teal-300/30 text-teal-100 text-sm tabular-nums" data-testid="skill-points">
              {points} SP
            </div>
            <Button
              size="sm"
              onClick={handleBuyPoints}
              disabled={busy || (credits || 0) < price}
              className="bg-amber-400/90 hover:bg-amber-300 text-slate-900 font-semibold"
              data-testid="buy-skill-point-button"
            >
              <Plus className="w-4 h-4 mr-1" /> Point · {price.toLocaleString()} cr
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="skilltree-close-button" className="text-slate-300 hover:text-white">
              <DoorOpen className="w-4 h-4 mr-1" /> Close
            </Button>
          </div>
        </div>

        {/* Branches */}
        <div className="overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {loading && <div className="col-span-3 text-center text-slate-400 py-10">Loading neural lattice…</div>}
          {!loading && BRANCH_ORDER.map((branch) => {
            const tone = BRANCH_TONE[branch] || BRANCH_TONE.Survival;
            return (
              <div key={branch} className={`rounded-xl border ${tone.ring} bg-white/[0.02] p-3`} data-testid={`branch-${branch.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                  <h3 className={`text-sm font-semibold uppercase tracking-wider ${tone.text}`}>{branch}</h3>
                </div>
                <div className="space-y-2.5">
                  {(byBranch[branch] || []).map((node) => {
                    const lvl = skills[node.id] || 0;
                    const maxed = lvl >= node.max_level;
                    const affordable = points >= node.cost;
                    const Icon = SKILL_ICON[node.id] || Brain;
                    return (
                      <div key={node.id} className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`skill-${node.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-slate-300" />
                            <span className="text-sm text-slate-100">{node.name}</span>
                          </div>
                          <Badge variant="outline" className="text-[10px] border-white/20 text-slate-300 tabular-nums">
                            {lvl}/{node.max_level}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1 leading-snug">{node.description}</p>
                        {/* tier pips */}
                        <div className="flex gap-1 mt-2">
                          {Array.from({ length: node.max_level }).map((_, i) => (
                            <span key={i} className={`h-1.5 flex-1 rounded-full ${i < lvl ? tone.dot : 'bg-white/10'}`} />
                          ))}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleUnlock(node)}
                          disabled={busy || maxed || !affordable}
                          className={`w-full mt-2.5 ${maxed ? 'bg-white/5 text-slate-500' : affordable ? 'bg-teal-500/90 hover:bg-teal-400 text-slate-900 font-semibold' : 'bg-white/5 text-slate-500'}`}
                          data-testid={`skill-upgrade-${node.id}`}
                        >
                          {maxed ? <><Check className="w-3.5 h-3.5 mr-1" /> Maxed</> : <>Upgrade · {node.cost} SP</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
